import {useEffect, useState} from "react";
import moment from "moment";

import EmptyData from "@/components/EmptyData";
import SwitchView from "@/components/SwitchView";
import VLFModal from "@/components/Modal";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFTextField from "@/components/Input/TextField";
import VLFDatePicker from "@/components/Input/DatePicker";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";

import {PlayerAPI} from "@/api/player";

import {
	DEFAULT_CATEGORY_ID,
	REQUIRED_FIELD,
	playerRoles,
} from "@/utils/constants";
import {checkRequiredFields, getTextField} from "@/utils/functions";
import {useTranslation} from "react-i18next";

function HandleReportModal(props) {
	const {open, confirm, close, internalTeams, initialData, players} = props;

	const {t} = useTranslation();

	const [formData, setFormData] = useState({});
	const [errors, setErrors] = useState([]);

	const [insertType, setInsertType] = useState(
		!initialData?.isInternal ? "single" : "multiple",
	);
	const [viewType, setViewType] = useState(
		initialData?.isInternal ? initialData?.matchCategoryId : "select",
	);

	const currentInternalTeam = internalTeams?.filter(
		(t) => t.playerCategoryId === initialData?.matchCategoryId,
	)?.[0];
	const categoryPlayers = currentInternalTeam?.players
		?.filter((p) => players?.find((tp) => tp.id === p.playerId))
		?.map((p) => p.player);

	const otherPlayers = players?.filter(
		(p) => p.categoryId !== initialData?.matchCategoryId,
	);

	const selectPlayers = initialData?.isInternal
		? viewType === initialData?.matchCategoryId
			? categoryPlayers
			: otherPlayers
		: players;

	const getFormattedPlayer = (p) => {
		return {
			value: p.id,
			label: p.firstName + " " + p.lastName,
			sortKey: p.lastName,
		};
	};

	const formattedPlayers = selectPlayers?.map((p) => {
		return getFormattedPlayer(p);
	});

	useEffect(() => {
		if (!open) {
			setFormData({});
		}
	}, [open]);

	useEffect(() => {
		if (insertType === "multiple") {
			const playersValues = {};
			categoryPlayers?.forEach((p) => {
				playersValues[p.id] = {
					player: getFormattedPlayer(p),
				};
			});
			setFormData({...formData, player: playersValues});
		}
	}, [insertType]);

	const createPlayer = async () => {
		try {
			const body = {
				playerCategoryId: DEFAULT_CATEGORY_ID,
				firstName: formData?.firstName ? formData.firstName : "",
				lastName: formData?.lastName,
				dateOfBirth: formData?.dateOfBirth
					? moment(formData?.dateOfBirth).format("YYYY-MM-DD")
					: undefined,
				primaryRole: formData?.playerPrimaryRole?.value,
				prospect: true,
			};

			const response = await PlayerAPI.create(body);
			return response;
		} catch (error) {
			console.log(error);
		}
	};

	const requiredFields =
		insertType === "multiple"
			? []
			: initialData?.isInternal || viewType === "select"
				? ["player", "playerNumber"]
				: ["lastName", "dateOfBirth", "playerPrimaryRole", "playerNumber"];

	const switchOptions = initialData?.isInternal
		? [
				{
					value: initialData?.matchCategoryId,
					label: initialData?.matchCategoryLabel,
				},
				{value: "others", label: "Altre categorie"},
			]
		: [
				{value: "new", label: "Nuovo"},
				{value: "select", label: "Esistente"},
			];

	const defaultTextField = {
		values: formData,
		setValues: setFormData,
		errors,
		setErrors,
	};

	return (
		<VLFModal
			maxWidth={insertType === "multiple" ? "md" : "sm"}
			open={open}
			title={"Aggiungi informazione"}
			close={close}
			confirm={async () => {
				const errors = checkRequiredFields(requiredFields, formData);
				setErrors(errors);

				if (!errors.length) {
					let body, selectedPlayer;
					if (insertType === "multiple") {
						body = [];
						Object.keys(formData?.player)?.forEach((p) => {
							if (
								formData?.player[p]?.player?.value &&
								formData?.player[p]?.playerNumber
							) {
								selectedPlayer = selectPlayers?.filter(
									(player) => player.id === p,
								)?.[0];
								body.push({
									playerId: formData.player[p].player.value,
									...formData.player[p],
									playerYear: moment(selectedPlayer?.dateOfBirth).year(),
								});
							}
						});
						confirm(body);
					} else {
						body = Object.assign({}, formData);

						if (viewType === "new") {
							const playerResponse = await createPlayer();
							selectedPlayer = playerResponse;
							body.player = {
								value: playerResponse?.id,
								label:
									playerResponse?.firstName +
									" " +
									playerResponse?.lastName +
									(initialData?.isInternal
										? " (" + playerResponse.category + ")"
										: ""),
							};
						} else {
							selectedPlayer = players?.filter(
								(p) => p.id === formData.player.value,
							)?.[0];
						}

						delete body?.firstName;
						delete body?.lastName;
						delete body?.dateOfBirth;
						delete body?.playerPrimaryRole;

						confirm([
							{
								...body,
								playerId: body.player.value,
								playerYear: moment(selectedPlayer?.dateOfBirth).year(),
							},
						]);
					}
				}
			}}
			content={
				<div className="row handle-report-modal-container">
					<div className="col-12">
						{initialData?.isInternal ? (
							<SwitchView
								currentSwitchView={insertType}
								setCurrentSwitchView={(value) => {
									setInsertType(value);

									if (value === "multiple") {
										setViewType(initialData?.matchCategoryId);
									}
								}}
								pageOptions={[
									{value: "single", label: "Singolo"},
									{value: "multiple", label: "Multiplo"},
								]}
								fixed
								callback={() => {
									setFormData({
										player: null,
									});
								}}
							/>
						) : null}
						{insertType === "single" ? (
							<SwitchView
								currentSwitchView={viewType}
								setCurrentSwitchView={setViewType}
								pageOptions={switchOptions}
								fixed
								callback={() => {
									setFormData({
										player: null,
									});
								}}
							/>
						) : null}
						{insertType === "multiple" ? (
							<>
								{!categoryPlayers?.length ? <EmptyData /> : null}
								{categoryPlayers
									?.sort((a, b) => {
										const nameA = a?.lastName.toLowerCase();
										const nameB = b?.lastName.toLowerCase();

										if (nameA < nameB) return -1;
										if (nameA > nameB) return 1;
										return 0;
									})
									?.map((p, k) => {
										return (
											<div key={k} className="col-sm-12 padding-small">
												<div className="player-row d-flex align-items-center">
													<div className="player-row-value big">
														<VLFSelectNew
															options={formattedPlayers}
															sortKey="sortKey"
															value={formData?.player?.[p.id]?.player || ""}
															onChange={(value) => {
																setFormData({
																	...formData,
																	player: {
																		...formData?.player,
																		[p.id]: {
																			...formData?.player[p.id],
																			player: value,
																		},
																	},
																});
															}}
															label="Giocatore"
														/>
													</div>
													<div className="player-row-value">
														<VLFTextField
															name="playerNumber"
															value={
																formData?.player?.[p.id]?.playerNumber || ""
															}
															onChange={(event) => {
																setFormData({
																	...formData,
																	player: {
																		...formData?.player,
																		[p.id]: {
																			...formData?.player[p.id],
																			playerNumber: event.target.value,
																		},
																	},
																});
															}}
															label="N° di maglia"
															size="small"
															type="number"
														/>
													</div>
													<div>
														<FormControlLabel
															control={
																<Checkbox
																	checked={
																		formData?.player?.[p.id]?.startingPlayer ||
																		false
																	}
																	onChange={(event) =>
																		setFormData({
																			...formData,
																			player: {
																				...formData?.player,
																				[p.id]: {
																					...formData?.player[p.id],
																					startingPlayer: event.target.checked,
																				},
																			},
																		})
																	}
																	inputProps={{"aria-label": "controlled"}}
																/>
															}
															label="Titolare"
														/>
													</div>
													{/* <div className="single-rpe-value">
														<VLFSelectNew
															options={rpeOptions}
															sorted={false}
															value={formData?.players?.[p.id]?.rpe || ""}
															onChange={(value) => {
																setFormData({
																	...formData,
																	players: {
																		...formData?.players,
																		[p.id]: {
																			...formData?.players[p.id],
																			rpe: value,
																		},
																	},
																});
															}}
															label="RPE"
														/>
													</div> */}
												</div>
											</div>
										);
									})}
							</>
						) : (
							<>
								{initialData?.isInternal ||
								(!initialData?.isInternal && viewType === "select") ? (
									<VLFSelectNew
										options={formattedPlayers}
										sortKey="sortKey"
										value={formData?.player || ""}
										onChange={(value) => {
											setErrors([]);
											setFormData({
												...formData,
												player: value,
											});
										}}
										required
										helperText={
											errors.indexOf("player") !== -1 ? REQUIRED_FIELD : ""
										}
										error={errors.indexOf("player") !== -1}
										label="Giocatore"
									/>
								) : (
									<>
										{getTextField({
											...defaultTextField,
											fieldKey: "firstName",
											label: t("name"),
										})}
										{getTextField({
											...defaultTextField,
											fieldKey: "lastName",
											label: t("surname"),
											required: true,
										})}
										<VLFDatePicker
											label={t("birthdate")}
											value={formData.dateOfBirth}
											onChange={(value) => {
												setErrors([]);
												setFormData({...formData, dateOfBirth: value});
											}}
											format="DD-MM-YYYY"
											required
											helperText={
												errors.indexOf("dateOfBirth") !== -1
													? REQUIRED_FIELD
													: ""
											}
											error={errors.indexOf("dateOfBirth") !== -1}
										/>
										<VLFSelectNew
											options={playerRoles}
											sortOrder="descend"
											value={formData.playerPrimaryRole}
											onChange={(newValue) => {
												setErrors([]);
												setFormData({
													...formData,
													playerPrimaryRole: newValue,
												});
											}}
											label={t("role")}
											required
											helperText={
												errors.indexOf("playerPrimaryRole") !== -1
													? REQUIRED_FIELD
													: ""
											}
											error={errors.indexOf("playerPrimaryRole") !== -1}
										/>
									</>
								)}
								<VLFTextField
									name="playerNumber"
									value={formData.playerNumber || ""}
									onChange={(event) => {
										setErrors([]);
										setFormData({
											...formData,
											playerNumber: event.target.value,
										});
									}}
									label="N° di maglia"
									size="small"
									type="number"
									required
									helperText={
										errors.indexOf("playerNumber") !== -1 ? REQUIRED_FIELD : ""
									}
									error={errors.indexOf("playerNumber") !== -1}
								/>
								<FormControlLabel
									control={
										<Checkbox
											checked={formData?.startingPlayer || false}
											onChange={(event) =>
												setFormData({
													...formData,
													startingPlayer: event.target.checked,
												})
											}
											inputProps={{"aria-label": "controlled"}}
										/>
									}
									label="Titolare"
								/>
							</>
						)}
					</div>
				</div>
			}
		/>
	);
}

export default HandleReportModal;
